/*
 * @Author: liuxw@codoon.com
 * @Date: 2020-10-30 13:49:25
 * @Last Modified by: liuxw@codoon.com
 * @Last Modified time: 2022-06-29 15:35:00
 * @Content:
 */

import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { System } from "open-codoon";
import classnames from "classnames";
import { IProgressRewardType, IRewordList } from "../../../service/Index";
import { IActivitySignState, IActivityState } from "../../interface";
import {
  getActivitySignState,
  getActivityState,
  getProgressRewardType,
} from "../../utils";
import day from "../../../utils/day";
import "./index.less";
import dayjs, { Dayjs } from "dayjs";
import { TIME_FORMATTER } from "../../../constants/time";
import { isIosBangsDevice } from "../../../utils/system";
import { IActivityInfo, IGoal } from "../../../service/activity/interface";
import {
  activityInfoSelector,
  gamblingCountSelector,
  gamblingPriceSelector,
  isGamblingSelector,
  tempBtnColorSelecor,
} from "../../../store/common/selector";
import { connect } from "react-redux";

const PREFIX = "FooterBtn";

type IProps = {
  selectedGoalNum: number;
  onStartBtnClick: () => void; // 点击开始运动
  onGamblingRegist: () => void; // 对赌报名
  isSignPage: boolean; // 是否为报名页面
  onLottery: () => void; // 去抽奖
  handleSubmit: (e: any, goal_id?: number) => void;
  onShowCertModal: (show: boolean) => void;
  onShowOtherModal: (show: boolean) => void;
  onShowRewardModal: (show: boolean) => void;
  rewordList: IRewordList;
  activity_id: string;
  goMoreAct: () => void; // 活动结束后，跳到更多活动
  onShowNewUserRewardModal: (show: boolean) => void; // 新用户大礼包弹窗
} & ReturnType<typeof mapStateToProps>;

type IBtnStateKey =
  | "registNotStart"
  | "regist"
  | "gamblingRegist"
  | "registEnd"
  | "activityNotStart"
  | "goalNotStart"
  | "start"
  | "goalCompleted"
  | "goalEnd"
  | "activityComplete"
  | "activityEnd"
  | "viewReward"
  | "viewOtherReward"
  | "receiveReward"
  | "lottery"
  | "PromotionReward"
  | "NewUserReward";

interface IBtnState {
  text: string; // 按钮文案
  isGrey: boolean; // 是否未灰色
  cb: null | Function; // 点击后的回调
}

const FooterBtn: FC<IProps> = memo(
  ({
    activityInfo,
    selectedGoalNum,
    handleSubmit,
    onShowCertModal,
    onShowOtherModal,
    onShowRewardModal,
    rewordList,
    onStartBtnClick,
    gamblingPrice,
    onGamblingRegist,
    isGambling,
    gamblingCount,
    isSignPage,
    onLottery,
    activity_id,
    tempBtnColor,
    goMoreAct,
    onShowNewUserRewardModal
  }) => {
    const system = useRef(new System());

    const [currTime, setCurrTime] = useState<Dayjs>(day());

    useEffect(() => {
      setCurrTime(day());
    }, [activityInfo?.enter_status, selectedGoalNum]);

    // 按钮各种状态
    const btnMap = useMemo((): Record<IBtnStateKey, IBtnState> => {
      return {
        registNotStart: {
          text: "报名未开始",
          isGrey: true,
          cb: null,
        },
        regist: {
          text: "立即报名",
          isGrey: false,
          cb: handleSubmit,
        },
        gamblingRegist: {
          text: `立即参加 ￥${(gamblingPrice / 100).toFixed(2)}`,
          isGrey: false,
          cb: onGamblingRegist,
        },
        registEnd: {
          text: "报名已结束",
          isGrey: true,
          cb: null,
        },
        activityNotStart: {
          text: "活动未开始",
          isGrey: true,
          cb: null,
        },
        // 目标
        goalNotStart: {
          text: "目标未开始",
          isGrey: true,
          cb: null,
        },
        start: {
          text: "马上开始",
          isGrey: false,
          cb: onStartBtnClick,
        },
        goalCompleted: {
          text: "目标已完成",
          isGrey: false,
          cb: null,
        },
        goalEnd: {
          text: "目标已结束",
          isGrey: true,
          cb: null,
        },
        activityComplete: {
          text: "活动已完成",
          isGrey: false,
          cb: null,
        },
        activityEnd: {
          text: "已结束，查看更多活动>",
          isGrey: false,
          cb: () => { goMoreAct() },
        },
        // 专指 证书
        viewReward: {
          text: "查看奖励",
          isGrey: false,
          cb: () => onShowCertModal(true),
        },
        // 专指 其它
        viewOtherReward: {
          text: "查看奖励",
          isGrey: false,
          cb: () => onShowOtherModal(true),
        },
        receiveReward: {
          text: "领取奖励",
          isGrey: false,
          cb: () => onShowRewardModal(true),
        },
        lottery: {
          text: "去抽奖",
          isGrey: false,
          cb: onLottery,
        },
        // 商品促销类型 统一显示为领取奖励
        PromotionReward: {
          text: "领取奖励",
          isGrey: false,
          cb: () => onShowRewardModal(true),
        },
        // 新用户大礼包奖励
        NewUserReward: {
          text: "领取奖励",
          isGrey: false,
          cb: () => onShowNewUserRewardModal(true),
        },
      };
    }, [
      handleSubmit,
      onShowCertModal,
      onShowRewardModal,
      onStartBtnClick,
      gamblingPrice,
    ]);

    // 当前目标
    const currGoal = useMemo((): IGoal | null => {
      if (!activityInfo) {
        return null;
      }
      const goals = activityInfo.goals;
      return goals[selectedGoalNum];
    }, [activityInfo, selectedGoalNum]);

    // 是否有奖 none无奖 medal奖章 other其他 cert证书 real实物奖励 virtual虚拟奖励（以前的逻辑，从活动模版判断是否有奖，先不动）
    const rewardType = useMemo((): IProgressRewardType | "none" => {
      return getProgressRewardType(rewordList);
    }, [rewordList]);

    // 当前展示的按钮
    const currBtn = useMemo((): IBtnState | null => {
      if (!activityInfo || !currGoal) {
        return null;
      }
      const currTimeVal = currTime.valueOf();
      // 报名时间
      let registStartTimeVal = day(
        activityInfo.enter_start,
        TIME_FORMATTER
      ).valueOf();
      let registEndTimeVal = day(
        activityInfo.enter_end,
        TIME_FORMATTER
      ).valueOf();
      // 活动时间
      const activityStartTimeVal = day(
        activityInfo.effect_start,
        TIME_FORMATTER
      ).valueOf();
      const activityEndTimeVal = day(
        activityInfo.effect_end,
        TIME_FORMATTER
      ).valueOf();
      // 目标时间
      const goalStartTimeVal = day(
        currGoal.limit_start,
        TIME_FORMATTER
      ).valueOf();
      const goalEndTimeVal = day(currGoal.limit_end, TIME_FORMATTER).valueOf();

      // 新人活动无活动报名的开始和结束时间，就用活动生效时间
      if (activityInfo.special_flag === 1) {
        registStartTimeVal = activityStartTimeVal
        registEndTimeVal = activityEndTimeVal
      }
      if (currTimeVal < registStartTimeVal) {
        return btnMap.registNotStart; // 报名未开始
      }

      if (
        registStartTimeVal <= currTimeVal &&
        currTimeVal <= registEndTimeVal
      ) {
        // 报名进行中
        if (!activityInfo.enter_status) {
          if (isGambling) {
            // 对赌报名
            return btnMap.gamblingRegist;
          } else {
            return btnMap.regist; // 立即报名
          }
        }
      }

      if (registEndTimeVal < currTimeVal) {
        // 报名已结束
        if (!activityInfo.enter_status) {
          // 未报名
          if (currTimeVal < activityEndTimeVal) {
            return btnMap.registEnd; // 报名已结束
          }
        }
      }

      if (activityInfo.enter_status) {
        // 已报名
        if (currTimeVal < activityStartTimeVal) {
          return btnMap.activityNotStart; // 活动未开始
        }
      }

      if (activityInfo.enter_status == 1) {
        // 已报名、还未完成
        if (
          activityStartTimeVal <= currTimeVal &&
          currTimeVal <= activityEndTimeVal
        ) {
          // 活动进行时间内
          if (currTimeVal < goalStartTimeVal) {
            if (activityInfo.special_flag === 1 && selectedGoalNum === 0) {
              //  && Math.abs(currTimeVal - goalStartTimeVal) < 1000
              return btnMap.start;
            } else {
              return btnMap.goalNotStart; // 目标未开始
            }
          } else if (
            goalStartTimeVal <= currTimeVal &&
            currTimeVal <= goalEndTimeVal
          ) {
            // 目标进行时间内
            if (currGoal.progress != 100) {
              // 目标还未完成
              return btnMap.start; // 马上开始
            } else {
              return btnMap.goalCompleted; // 目标已完成
            }
          } else if (goalEndTimeVal < currTimeVal) {
            // 目标已结束
            if (currGoal.progress != 100) {
              // 还未完成
              return btnMap.goalEnd; // 目标已结束
            }
          }
        }
      }

      if (activityInfo.enter_status == 2) {
        /* 活动已完成 */
        // 商品促销类型的活动 - 底部按钮统一显示为领取奖励 - 弹窗
        if (activityInfo.type == 2) {
          return btnMap.PromotionReward; // 领取奖励
        }

        // 没有奖励 或 奖章
        if (rewardType == "none" || rewardType === "medal") {
          return btnMap.activityComplete; // 活动已完成
        }

        // 虚拟奖励
        if (rewardType === "real" || rewardType === "virtual") {
          return btnMap.receiveReward; // 领取奖励
        }
        // 抽奖活动
        if (rewardType === "sync_with_sign_page") {
          return btnMap.lottery; // 去抽奖
        }
        if (rewardType === "cert") {
          return btnMap.viewReward; // 查看奖励（证书）
        }
        if (rewardType === "other") {
          return btnMap.viewOtherReward; // 查看奖励（其它）
        }
        // 新人大礼包
        if (activityInfo.special_flag === 1 && rewardType === 'new_comer_gift_pack') {
          return btnMap.NewUserReward; // 领取奖励
        }
      }

      if (activityEndTimeVal < currTimeVal) {
        // 活动已结束
        if (activityInfo.enter_status != 2) {
          // 没完成 或 没报名
          return btnMap.activityEnd; // 活动已结束
        }
      }

      return null;
    }, [currTime, activityInfo, btnMap, rewardType, currGoal, isGambling]);

    return currBtn ? (
      <div className={classnames(PREFIX)}>
        {/* 对赌奖金信息 */}
        {isSignPage && isGambling ? (
          <div className={`${PREFIX}-gamblingTotalPrice`}>
            <p>
              总奖金池
              <span className={`${PREFIX}-gamblingTotalPriceNum`}>
                {gamblingCount ? (gamblingCount.amount || 0) / 100 : "--"}
              </span>
              元,完成挑战即可瓜分
            </p>
          </div>
        ) : null}
        {/* 按钮 */}
        <div className={`${PREFIX}-btnWrap`}>
          <div
            className={classnames(`${PREFIX}-btn`, {
              "is-iphonex": isIosBangsDevice,
              "is-grey": currBtn.isGrey,
              "is-clickable": !!currBtn.cb && !currBtn.isGrey, // 有回调函数，且不是置灰状态，点击才有效果
              special: activity_id === "423",
            })}
            onClick={() => {
              currBtn.cb && currBtn.cb();
            }}
            style={{
              backgroundColor: tempBtnColor,
            }}
          >
            <div className={`${PREFIX}-btnContent`}>{currBtn.text}</div>
          </div>
        </div>
      </div>
    ) : null;
  }
);

const mapStateToProps = (state: any) => {
  return {
    activityInfo: activityInfoSelector(state),
    isGambling: isGamblingSelector(state),
    gamblingPrice: gamblingPriceSelector(state),
    gamblingCount: gamblingCountSelector(state),
    tempBtnColor: tempBtnColorSelecor(state),
  };
};

export default connect(mapStateToProps)(FooterBtn);
